import { ReactNode, useRef, useState } from 'react';

import { MaximizeIcon } from '@cloudquery/base-icons';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { useTheme, SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GridCloseIcon } from '@mui/x-data-grid';
import { QueryObserverSuccessResult, UseQueryResult } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  Loader,
  MenuActions,
  useBooleanState,
  HoverInfo,
  paths,
  generateUrl,
  SessionStorageItems,
  useDebounce,
  getEnv,
} from 'shared/base';

import { TableDataExportDialog } from '@/components/tableDataExportDialog';

import { CARD_HEADER_HEIGHT, CHART_CONTENT_HEIGHT } from '../constants';
import { CardError } from './cardError';

interface Props<T> {
  clearFilters?: () => void;
  contentSx?: SxProps;
  description?: string;
  onMaximize?: () => void;
  onMinimize?: () => void;
  query: string;
  queryResult: UseQueryResult<T, any> | UseQueryResult<T, any>[];
  renderContent: (result: QueryObserverSuccessResult<T, any>) => ReactNode;
  renderContentArray?: (results: QueryObserverSuccessResult<T, any>[]) => ReactNode;
  renderLoading?: () => ReactNode;
  searchTerm?: string;
  setSearchTerm?: (value: string) => void;
  showMaximize?: boolean;
  showSearch?: boolean;
  sx?: SxProps;
  title: string;
}

const getQueryStatus = <T,>(queryResult: UseQueryResult<T, any> | UseQueryResult<T, any>[]) => {
  if (!Array.isArray(queryResult)) {
    return {
      error: queryResult.error,
      isArray: false,
      status: queryResult.status,
    };
  }

  const errorResult = queryResult.find((result) => result.status === 'error');
  if (errorResult) {
    return {
      error: errorResult.error,
      isArray: true,
      status: 'error',
    };
  }

  if (queryResult.some((result) => result.status === 'loading')) {
    return {
      isArray: true,
      status: 'loading',
    };
  }

  if (queryResult.every((result) => result.status === 'success')) {
    return {
      isArray: true,
      status: 'success',
    };
  }

  return {
    isArray: true,
    status: 'unknown',
  };
};

export const CardWrapper = <T,>({
  clearFilters,
  contentSx,
  description,
  onMaximize,
  onMinimize,
  query,
  queryResult,
  renderContent,
  renderContentArray,
  renderLoading,
  searchTerm,
  setSearchTerm,
  showMaximize = false,
  showSearch = false,
  sx,
  title,
}: Props<T>) => {
  const menuRef = useRef(null);
  const theme = useTheme();
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [menuShown, showMenu, hideMenu] = useBooleanState(false);
  const { currentMembership } = useBaseAuthenticated();
  const [maximized, setMaximized] = useState(false);
  const [searchValue, setSearchValue] = useState(searchTerm);
  const { debouncedFunc: updateDebouncedSearchTerm } = useDebounce(
    setSearchTerm || (() => {}),
    1000,
  );

  const handleMaximize = () => {
    setMaximized(true);
    onMaximize?.();
  };

  const handleMinimize = () => {
    setMaximized(false);
    onMinimize?.();
  };

  const handleSearchTermChange = (value: string) => {
    setSearchValue(value);
    updateDebouncedSearchTerm?.(value);
  };

  return (
    <Box
      sx={{
        ...(maximized
          ? {
              backgroundColor: 'background.paper',
              bottom: 0,
              height: '100%',
              left: 0,
              position: 'fixed',
              right: 0,
              top: 0,
              width: '100%',
              zIndex: 1000,
            }
          : {}),
      }}
    >
      <TableDataExportDialog
        fetchOptions={{
          baseUrl: `${getEnv(
            'NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL',
            process.env.NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL,
            '',
          )}/teams/default/queries/execute`,
          data: JSON.stringify({ query }),
          method: 'POST',
        }}
        onClose={() => setShowExportDialog(false)}
        open={showExportDialog}
      />
      <Stack
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          minWidth: '33%',
          ...sx,
          ...(maximized && {
            border: '1px solid',
            borderColor: theme.palette.divider,
            height: '95%',
            marginLeft: '2.5%',
            position: 'fixed',
            top: '2.5%',
            width: '95%',
          }),
        }}
      >
        <Stack
          alignItems="center"
          borderBottom="1px solid"
          borderColor="divider"
          direction="row"
          height={CARD_HEADER_HEIGHT}
          justifyContent="space-between"
          paddingBlockEnd={1.5}
          paddingBlockStart={1.5}
          paddingInlineEnd={1.5}
          paddingInlineStart={2.5}
          sx={{
            backgroundColor: 'secondary.darkMedium',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography variant="h6">{title}</Typography>
            {description && <HoverInfo description={description} title={title} />}
          </Stack>
          <Stack direction="row" spacing={1}>
            {showSearch && (
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    '& input': {
                      padding: '0',
                    },
                    height: '40px',
                  },
                }}
                onChange={(e) => handleSearchTermChange?.(e.target.value)}
                placeholder="Search"
                size="small"
                value={searchValue}
                variant="outlined"
              />
            )}
            {showMaximize && !maximized && (
              <Button
                color="inherit"
                onClick={handleMaximize}
                size="small"
                sx={{ minWidth: 'auto', padding: 0.5 }}
              >
                <MaximizeIcon />
              </Button>
            )}

            <IconButton
              ref={menuRef}
              aria-expanded={menuShown ? 'true' : undefined}
              aria-haspopup="true"
              color="inherit"
              onClick={showMenu}
              size="small"
              sx={{ padding: 0.5 }}
            >
              <MoreVertIcon />
            </IconButton>
            <MenuActions
              actions={[
                {
                  id: 'go-to-sql-console',
                  label: 'Open in SQL console',
                  onClick: () => {
                    sessionStorage.setItem(SessionStorageItems.NewSQLConsoleQuery, query);
                    window.open(
                      generateUrl({
                        pathname: paths.platform.team.sqlConsole.index,
                        query: {
                          team: currentMembership.team.name,
                        },
                      }),
                      '_blank',
                    );
                  },
                },
                {
                  id: 'copy-query',
                  label: 'Copy query to clipboard',
                  onClick: () => {
                    navigator.clipboard.writeText(query);
                    toast.success('Query copied to clipboard');
                  },
                },
                {
                  id: 'export',
                  label: 'Export',
                  onClick: () => {
                    setShowExportDialog(true);
                  },
                },
              ]}
              anchorEl={menuRef.current}
              id="resource-detail-actions-menu"
              onClose={hideMenu}
              open={menuShown}
              toggleId="resource-detail-actions-menu-toggle"
            />
            {maximized && (
              <Button
                color="inherit"
                onClick={handleMinimize}
                size="small"
                sx={{ minWidth: 'auto', padding: 0.5 }}
              >
                <GridCloseIcon />
              </Button>
            )}
          </Stack>
        </Stack>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: CHART_CONTENT_HEIGHT,
            minWidth: '33%',
            padding: '1.25rem',
            ...contentSx,
          }}
        >
          {(() => {
            const { error, isArray, status } = getQueryStatus(queryResult);
            if (error?.data.message) {
              return <CardError clearFilters={clearFilters} error={error} />;
            } else if (status === 'loading') {
              return renderLoading ? renderLoading() : <Loader />;
            } else {
              return isArray
                ? renderContentArray?.(queryResult as QueryObserverSuccessResult<T, any>[])
                : renderContent(queryResult as QueryObserverSuccessResult<T, any>);
            }
          })()}
        </Box>
      </Stack>
    </Box>
  );
};
